import data from "./shapes/dragon.json";
import {
  epicycles,
  sampleTrail,
  toSvgPath,
  visibleTrail,
} from "./math/epicycles";
import { clamp01 } from "./timeline";

export const dragonTiming = { start: 1.5, drawing: 30, duration: 36 };
export const dragonLayout = {
  x: 540,
  y: 1050,
  scale: 940 / (data.bounds[2] - data.bounds[0]),
  centerX: (data.bounds[0] + data.bounds[2]) / 2,
  centerY: (data.bounds[1] + data.bounds[3]) / 2,
};
const laneEnds = [0, 0, 0, 0];
const rawStrokes = data.strokes.map((stroke, id) => {
  const lane = laneEnds.indexOf(Math.min(...laneEnds));
  const start = laneEnds[lane];
  const duration = 0.16 + stroke.length / 750;
  laneEnds[lane] += duration;
  return { ...stroke, id, lane, start, duration };
});

export const dragonStrokes = rawStrokes.map((stroke) => {
  const terms = stroke.terms.map(([frequency, amplitude, phase]) => ({
    frequency,
    amplitude,
    phase,
    x: amplitude * Math.cos(phase),
    y: amplitude * Math.sin(phase),
  }));
  const dc = { x: stroke.dc[0], y: stroke.dc[1] };
  // Open source strokes are mirrored before the DFT: draw the outward half-cycle
  // only. This avoids closing a line across the eyes, whiskers or disconnected claws.
  const phaseEnd = stroke.closed ? 1 : 0.5;
  const resolution = Math.max(
    32,
    Math.min(4096, Math.ceil((stroke.length * 1.5) / phaseEnd)),
  );
  const fullCycle = sampleTrail(terms, dc, resolution);
  const trail = fullCycle.slice(0, Math.floor(resolution * phaseEnd) + 1);
  const finalChain = epicycles(terms, dc, phaseEnd);
  trail[trail.length - 1] = finalChain[finalChain.length - 1].tip;
  return {
    id: stroke.id,
    lane: stroke.lane,
    terms,
    dc,
    phaseEnd,
    trail,
    start:
      dragonTiming.start +
      (stroke.start / laneEnds[stroke.lane]) * dragonTiming.drawing,
    duration: (stroke.duration / laneEnds[stroke.lane]) * dragonTiming.drawing,
    fullPath: toSvgPath(trail),
    rms: stroke.rms,
  };
});
export const dragonFullPath = dragonStrokes.map((s) => s.fullPath).join(" ");
export const getDragonFrame = (seconds: number) => {
  const complete = dragonStrokes.filter((s) => seconds >= s.start + s.duration);
  const active = dragonStrokes
    .filter((s) => seconds >= s.start && seconds < s.start + s.duration)
    .map((s) => {
      const progress = clamp01((seconds - s.start) / s.duration);
      const chain = epicycles(s.terms, s.dc, progress * s.phaseEnd);
      const tip = chain[chain.length - 1].tip;
      return {
        ...s,
        progress,
        chain,
        tip,
        path: toSvgPath(visibleTrail(s.trail, progress, tip)),
      };
    });
  return { complete, active };
};
